/**
 * Booking deep link to one exact slot (One-Stop v2 "openings" rows, 2026-09-25).
 *
 * `/<journey>?service=<id>&date=YYYY-MM-DD&time=HH:mm&tz=<IANA>&staff=<id?>`
 *
 * Civil date + time + IANA zone, never an instant, so a link minted in one
 * zone names the same wall-clock slot wherever it is opened. The link is
 * NAVIGATIONAL ONLY: it never books, and it is honoured only when the live
 * availability window still offers a matching slot.
 *
 * PARAM NAMES ARE PROPOSED, NOT CONFIRMED (PR #2258). They live here and
 * nowhere else. When CC adds them to `JOURNEY_HANDOFF_PARAMS`, read from there.
 */
export const SLOT_DEEPLINK_PARAMS = { date: "date", time: "time", tz: "tz", staff: "staff" } as const;

/** `service` is the linked service id; the slot applies only to that service's window (CC 0030 item 11). */
export type SlotDeepLink = { service: string; date: string; time: string; tz: string; staff: string };

type OfferLike = { startUtc?: string; staffId?: string | null; locationId?: string | null };
type WindowLike = { byDay?: Record<string, OfferLike[] | undefined> } | null | undefined;

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

function isRealDate(date: string): boolean {
  const m = DATE_RE.exec(date);
  if (!m) return false;
  const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
  return d.getUTCFullYear() === +m[1] && d.getUTCMonth() === +m[2] - 1 && d.getUTCDate() === +m[3];
}

function isZone(tz: string): boolean {
  if (!tz) return false;
  try { new Intl.DateTimeFormat("en-US", { timeZone: tz }); return true; } catch { return false; }
}

/** The slot part of the query, or null when any required part is missing or
 *  malformed. Then the link degrades to the plain `?service=` behaviour. */
export function readSlotDeepLink(search: string): SlotDeepLink | null {
  let q: URLSearchParams;
  try { q = new URLSearchParams(search); } catch { return null; }
  const date = (q.get(SLOT_DEEPLINK_PARAMS.date) || "").trim();
  const time = (q.get(SLOT_DEEPLINK_PARAMS.time) || "").trim();
  const tz = (q.get(SLOT_DEEPLINK_PARAMS.tz) || "").trim();
  const staff = (q.get(SLOT_DEEPLINK_PARAMS.staff) || "").trim();
  const service = (q.get("service") || "").trim();
  if (!service || !isRealDate(date) || !TIME_RE.test(time) || !isZone(tz)) return null;
  return { service, date, time, tz, staff };
}

/** An instant's civil date and time in an IANA zone. */
export function civilIn(startUtc: string, tz: string): { date: string; time: string } | null {
  const ms = Date.parse(startUtc);
  if (Number.isNaN(ms)) return null;
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23",
  }).formatToParts(new Date(ms));
  const get = (type: string) => (parts.find((p) => p.type === type) || { value: "" }).value;
  return { date: `${get("year")}-${get("month")}-${get("day")}`, time: `${get("hour")}:${get("minute")}` };
}

/**
 * The offered slot the link names, with the `byDay` key it sits under, or
 * null when the window no longer offers it. Offers are matched on their own
 * `startUtc` read in the link's zone, so it doesn't matter which zone
 * `byDay` is keyed in. Staff narrows the match only when both the link and
 * the offer name one: availability is store-level in phase 1, and offers
 * usually carry no staff.
 */
export function findDeepLinkedSlot<T extends OfferLike>(
  window: WindowLike,
  want: SlotDeepLink,
  bookable: (slot: T) => boolean,
): { day: string; slot: T } | null {
  const byDay = (window && window.byDay) || {};
  for (const day of Object.keys(byDay)) {
    for (const slot of (byDay[day] || []) as T[]) {
      if (!slot || typeof slot.startUtc !== "string" || !bookable(slot)) continue;
      if (want.staff && slot.staffId && String(slot.staffId) !== want.staff) continue;
      const civil = civilIn(slot.startUtc, want.tz);
      if (civil && civil.date === want.date && civil.time === want.time) return { day, slot };
    }
  }
  return null;
}
